import type {
    SearchPayload,
    Table,
    TableDataSearchResponse,
    OpenSearchListResponse,
    TableFieldsResponse,
    TableListResponse,
    TableRow
} from '../types/tables';
import { slugify } from '../utils/string';
import { APIClient } from 'vince-core/api/apiClient';
import { VL_API_BASE_URL } from 'vince-core/constants/backendUrls';
import { type Primitive, SortDirection } from 'vince-core/types/common';
import { inferSchema } from 'vince-core/utils/tables';
import type { Query, QueryResponse } from 'vince-core/types/table-generic-search';

const baseUrl = `${VL_API_BASE_URL}/v1/custom-tables`;

export async function getTablesList () {
    return await APIClient<TableListResponse>(`${baseUrl}/meta`);
}

export async function getTableDetails (tableName: string): Promise<Table> {
    return await APIClient<Table>(`${baseUrl}/meta/${tableName}`);
}

export async function findTableData (payload: SearchPayload): Promise<OpenSearchListResponse> {
    return await APIClient<OpenSearchListResponse>(`${baseUrl}/search/${payload.tableName}`, {
        method: 'POST',
        body: JSON.stringify(payload.body)
    });
}

export async function findTableDataGet (tableName: string, query: string, start: number, size: number, sortDirection: SortDirection, sortValue?: string): Promise<TableDataSearchResponse> {
    const q = new URLSearchParams({
        q: query,
        size: size + '',
        ...(sortValue ? { sort: sortValue } : {}),
        order: sortDirection.toString()
    });

    if (start > 0) {
        q.set('from', start + '');
    }

    return await APIClient<TableDataSearchResponse>(`${baseUrl}/search/${tableName}?${q.toString()}`);
}

export async function getTableFields (tableName: string): Promise<TableFieldsResponse> {
    const result = await APIClient<TableFieldsResponse>(`${baseUrl}/meta/${tableName}/fields`);

    result.items.forEach((field) => {
        if (!field.schemaType) {
            const { type, format } = inferSchema(field);
            field.schemaFormat = format;
            field.schemaType = type;
        }
    });

    return result;
}

export async function truncateTable (tableName: string) {
    await APIClient(`${baseUrl}/meta/${slugify(tableName)}/truncate`, { method: 'DELETE' });
}

export async function deleteTable (tableName: string) {
    await APIClient(`${baseUrl}/meta/${slugify(tableName)}`, { method: 'DELETE' });
}

export async function saveTable (table: Table, isEdit: boolean): Promise<void> {
    if (isEdit) {
        return await APIClient(`${baseUrl}/meta/${table.tableName}`, {
            method: 'PATCH',
            body: table
        });
    } else {
        return await APIClient(`${baseUrl}/meta`, {
            method: 'POST',
            body: table
        });
    }
}

export async function uploadDataFromFile (tableName: string, excelFile: File) {
    const formData = new FormData();
    formData.append('excel', excelFile);

    return await APIClient<{ rowsInserted: number, s3Key: string }>(`${baseUrl}/data/${tableName}/file`, {
        method: 'POST',
        body: formData
    });
}

export async function deleteTableRows (tableName: string, rows: Partial<TableRow>[]) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'DELETE',
        body: JSON.stringify(rows)
    });
}

export async function updateRow (tableName: string, body: Record<string, Primitive | undefined>) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'PUT',
        body: JSON.stringify(body)
    });
}

export async function updatePartialRow (tableName: string, body: Record<string, Primitive | undefined>) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
    });
}

export async function genericSearch (tableName: string, query: Query) {
    return await APIClient<QueryResponse>(`${baseUrl}/generic-search/${tableName}`, {
        method: 'POST',
        body: JSON.stringify(query)
    });
}

